import type { Idea } from '../api/types'
import { isFilmable } from './formats'

/**
 * The four stages the pipeline is worked in, and which one an idea is in.
 *
 * Status is the server's record of the video pipeline; the stages are how
 * Martin works it. Filming is not a status: it is the next batch, the ideas
 * queued for the next time he films, and they keep their status (idea or
 * scripted) until the batch is marked filmed. Filmed and edited are one
 * stage, To post: the video is made or on its way, and posting it is what is
 * left to do.
 *
 * Published and killed ideas have left the pipeline, and an idea only for
 * LinkedIn was never in it: that is written, not filmed, and lives on the
 * LinkedIn list.
 */
export const STAGES = ['ideas', 'scripted', 'filming', 'post'] as const
export type Stage = (typeof STAGES)[number]

export const STAGE_LABEL: Record<Stage, string> = {
  ideas: 'Ideas',
  scripted: 'Scripted',
  filming: 'Filming',
  post: 'To post',
}

/** One idea's place, as the editor's step row names it. */
export const STEP_LABEL: Record<Stage, string> = {
  ideas: 'Idea',
  scripted: 'Scripted',
  filming: 'Filming',
  post: 'To post',
}

export function stageOf(idea: Idea, queue: readonly number[]): Stage | null {
  if (!isFilmable(idea)) return null
  switch (idea.status) {
    case 'idea':
    case 'scripted':
      if (queue.includes(idea.id)) return 'filming'
      return idea.status === 'idea' ? 'ideas' : 'scripted'
    case 'filmed':
    case 'edited':
      return 'post'
    default:
      return null
  }
}

/**
 * The ideas in each stage. Ideas and Scripted keep the list's order (newest
 * status change first); Filming is the order the batch will be read in; To
 * post puts what is ready to post above what is still being edited.
 */
export function byStage(ideas: readonly Idea[], queue: readonly number[]): Record<Stage, Idea[]> {
  const stages: Record<Stage, Idea[]> = { ideas: [], scripted: [], filming: [], post: [] }
  for (const idea of ideas) {
    const stage = stageOf(idea, queue)
    if (stage) stages[stage].push(idea)
  }
  stages.filming.sort((a, b) => queue.indexOf(a.id) - queue.indexOf(b.id))
  stages.post.sort((a, b) => Number(b.status === 'edited') - Number(a.status === 'edited'))
  return stages
}

/** Ideas that go straight to the camera: one card, one spoken line. */
export function isQuickToFilm(idea: Pick<Idea, 'format'>): boolean {
  return idea.format === 'question' || idea.format === 'term'
}
